import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { BalanceRequest } from 'src/app/Interface/DataEntity/DataEntityBalanceForm_Interface';
import { DataEntityBalanceFormService } from 'src/app/Service/DataEntity/DataEntityBalanceForm.service';
import { AlertifyService } from 'src/app/Service/Alertify.service';
import { acceptanceLimits, finiteNumber, nonNegative, optionalPositive, positive, repeatReadings, uniquePoints } from './data-entity-balance-form.validators';
import { balanceHelp, balanceLabels } from './data-entity-balance-form.help';

@Component({ selector: 'app-data-entity-balance-form', templateUrl: './DataEntityBalanceForm.component.html', styleUrls: ['./DataEntityBalanceForm.component.scss'] })
export class DataEntityBalanceFormComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoadingInitialData = false;
  isSubmitting = false;
  isDownloading = false;
  downloadingPath = '';
  expandedResults = new Set<number>([0]);
  private resetSnapshot: any = {};
  private resetOutput: any = {};
  pendingAction: 'preview' | 'save' = null;
  readonly collapsedPoints = new WeakSet<AbstractControl>();
  readonly environmentFields = ['Temperature', 'Humidity', 'Pressure'];
  submitAttempted = false;
  errorMessage = '';
  legacyWarning = '';
  result: any = null;
  referenceStandards: any[] = [];
  certificateFilePath = '';
  uncertaintyReportFilePath = '';
  readonly help = balanceHelp;
  readonly labels = balanceLabels;
  readonly generalFields = ['Description', 'Expert', 'IssueDateMiladi', 'CalibrationDateMiladi', 'RecalibrationDateMiladi', 'CustomerName', 'CertificateAddress', 'LabAdderss'];
  readonly deviceFields = ['Manufacturer', 'ModelDevice', 'TypeDevice', 'SerialNoDevice', 'CodeNoDevice'];
  readonly rangeFields = ['Value', 'From', 'To', 'Division', 'UncertainyFloat'];
  readonly systemFields = ['RequestId', 'LetterId', 'CalibrationDetailId'];
  readonly eccentricityFields = ['Lecc', 'ICenter', 'IFront_L', 'IBack_L', 'IFront_R', 'IBack_R'];
  readonly pointTypeBFields = ['Dmcal_D', 'DmB', 'DIresolution'];
  readonly sharedTypeBFields = ['DIresolution_0', 'Dk'];
  readonly acceptanceFields = ['Tolerance', 'Limit', 'LowerLimit', 'UpperLimit', 'LimitValue', 'DecisionRule', 'Distribution'];
  readonly acceptanceNumbers = ['Tolerance', 'Limit', 'LowerLimit', 'UpperLimit', 'LimitValue'];
  readonly distributionTypes = [{ value: 'Automatic', label: 'خودکار' }, { value: 'Normal', label: 'نرمال' }, { value: 'Rectangular', label: 'مستطیلی' }, { value: 'Trapezoidal', label: 'ذوزنقه‌ای' }, { value: 'Triangular', label: 'مثلثی' }, { value: 'StudentT', label: 'تی استیودنت' }];
  readonly decisionRules = ['پذیرش سخت‌گیرانه / رد سخت‌گیرانه', 'پذیرش ساده / رد ساده', 'پذیرش سخت‌گیرانه / رد آسان‌گیرانه', 'پذیرش آسان‌گیرانه / رد سخت‌گیرانه', 'پذیرش سخت‌گیرانه / رد ساده', 'پذیرش ساده / رد سخت‌گیرانه'];
  private overrides = new WeakSet<AbstractControl>();
  private applyingDefaults = false;
  private readonly destroy$ = new Subject<void>();
  private formSubscriptions = new Subscription();
  private loadSubscription: Subscription;
  private routeId: number;
  private inputId: number;
  private readonly controlIds = new WeakMap<AbstractControl, string>();
  private nextControlId = 0;

  @Input() set entityId(value: number) { this.inputId = this.positiveId(value); if (this.form) { this.load(); } }
  get entityId(): number { return this.inputId; }
  constructor(private fb: FormBuilder, private service: DataEntityBalanceFormService, private alertify: AlertifyService, private route: ActivatedRoute, private host: ElementRef) {}
  ngOnInit(): void {
    this.initializeForm();
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => { this.routeId = this.positiveId(params.get('id')); this.load(); });
  }
  ngOnDestroy(): void { this.loadSubscription?.unsubscribe(); this.formSubscriptions.unsubscribe(); this.destroy$.next(); this.destroy$.complete(); }
  @HostListener('wheel', ['$event']) onWheel(event: WheelEvent): void {
    const input = event.target as HTMLInputElement;
    if (input?.type === 'number' && document.activeElement === input) { input.blur(); }
  }
  get Points(): FormArray { return this.form.get('Points') as FormArray; }
  get readings(): FormArray { return this.form.get('DRrepeatability.Observations') as FormArray; }
  get codes(): FormArray { return this.form.get('BehfaDeviceCodes') as FormArray; }
  asGroup(control: AbstractControl): FormGroup { return control as FormGroup; }
  controlId(control: AbstractControl): string {
    if (!this.controlIds.has(control)) { this.controlIds.set(control, 'balance-field-' + (++this.nextControlId)); }
    return this.controlIds.get(control);
  }
  nModel(row: AbstractControl): FormGroup { return row.get('NModel') as FormGroup; }
  trackByControl(_index: number, control: AbstractControl): AbstractControl { return control; }

  initializeForm(source: any = {}): void {
    this.formSubscriptions.unsubscribe(); this.formSubscriptions = new Subscription();
    this.overrides = new WeakSet<AbstractControl>();
    this.submitAttempted = false; this.result = null; this.errorMessage = '';
    this.expandedResults = new Set<number>([0]);
    const s = this.normalize(source);
    this.legacyWarning = s.Dm_cal?.length ? 'این مدل دارای ورودی قدیمی عدم‌قطعیت است. اطلاعات گواهی وزنه‌های مرجع را بازبینی و تأیید کنید.' : '';
    const controls: any = {};
    [...this.generalFields, ...this.deviceFields].forEach(key => controls[key] = [s[key] ?? '', Validators.required]);
    this.systemFields.forEach(key => controls[key] = [{ value: s[key] ?? (key === 'RequestId' ? '' : 0), disabled: true }]);
    this.rangeFields.forEach(key => controls[key] = [s[key] ?? (key === 'UncertainyFloat' ? 2 : ''), finiteNumber]);
    controls.UncertainyFloat = [s.UncertainyFloat ?? 2, [nonNegative, Validators.max(10), Validators.pattern(/^\d+$/)]];
    this.environmentFields.forEach(key => controls[key] = [s[key] ?? null, control => control.value == null || String(control.value).trim() === '' ? null : finiteNumber(control)]);
    controls.Unit = [{ value: s.Unit ?? 'g', disabled: true }, Validators.pattern(/^g$/)];
    controls.Farsi = [s.Farsi ?? true];
    controls.BehfaDeviceCodes = this.fb.array((s.BehfaDeviceCodes || []).map(code => this.fb.control(code, Validators.required)));
    controls.Points = this.fb.array([]);
    controls.DIresolution_0 = this.rectangular(s.DIresolution_0);
    controls.Dk = this.rectangular(s.Dk);
    this.eccentricityFields.forEach(key => controls[key] = [s[key]?.Value ?? '', key === 'Lecc' ? positive : finiteNumber]);
    controls.Rrepeatability = [s.Rrepeatability?.Value ?? '', finiteNumber];
    controls.Lrep = [s.Lrep?.Value ?? '', optionalPositive];
    controls.DRrepeatability = this.fb.group({
      Observations: this.fb.array((s.DRrepeatability?.Observations?.length ? s.DRrepeatability.Observations : ['', '']).map(x => this.fb.control(x, finiteNumber))),
      UncertaintyEstimate: [s.DRrepeatability?.UncertaintyEstimate ?? 0, [Validators.required, Validators.pattern(/^[01]$/)]]
    }, { validators: repeatReadings });
    this.form = this.fb.group(controls, { validators: uniquePoints });
    if (Array.isArray(s.NModels) && s.NModels.length) {
      s.NModels.forEach((n, i) => this.addPoint({ NModel: n, IIndication: s.IIndication?.[i]?.Value, Wm_cr: s.Wm_cr?.[i], Dmcal_D: s.Dmcal_D?.[i], DmB: s.DmB?.[i], DIresolution: s.DIresolution?.[i] }, false));
      this.Points.controls.slice(1).forEach(row => {
        const overridden = Array.isArray(s.AcceptanceOverrides)
          ? s.AcceptanceOverrides.includes(row.get('NModel.MeasurementId').value)
          : JSON.stringify(this.acceptanceValue(this.nModel(row))) !== JSON.stringify(this.acceptanceValue(this.nModel(this.Points.at(0))));
        if (overridden) { this.overrides.add(row); }
      });
    } else { this.addPoint(); }
    const id = this.inputId || this.routeId;
    if (id) { this.form.get('CalibrationDetailId').setValue(id); }
    this.certificateFilePath = s.Certificatefile || '';
    this.uncertaintyReportFilePath = s.uncertaintyReportFile || '';
    this.resetSnapshot = s; this.rememberResetOutput();
    this.formSubscriptions.add(this.form.valueChanges.subscribe(() => { this.result = null; this.certificateFilePath = ''; this.uncertaintyReportFilePath = ''; }));
  }

  private distribution(value: any = {}): FormGroup {
    return this.fb.group({
      $type: [value.$type ?? 'Normal', [Validators.required, Validators.pattern(/^(Automatic|Normal|Rectangular|Trapezoidal|Triangular|StudentT)$/)]],
      CoverageFactor: [value.CoverageFactor ?? 2, positive], ShapeFactor: [value.ShapeFactor ?? 1, nonNegative],
      DoF: [value.DoF ?? 10, positive]
    });
  }
  private rectangular(s: any = {}): FormGroup {
    s = s || {};
    return this.fb.group({ Value: [s.Value ?? 0, finiteNumber], HalfWidthOfLimit: [s.HalfWidthOfLimit ?? '', nonNegative], RelativeErrorOfUncertainty: [s.RelativeErrorOfUncertainty ?? 0, finiteNumber] });
  }
  addPoint(value: any = {}, inherit = true): void {
    const n = value.NModel || {}, w = value.Wm_cr || {};
    let id = 1; while (this.Points.controls.some(row => row.get('NModel.Value').value === 'p' + id || row.get('NModel.MeasurementId').value === 'p' + id)) { id++; }
    const model = this.fb.group({
      MeasurementId: [n.MeasurementId || n.Value || 'p' + id, Validators.required],
      Value: [n.Value || 'p' + id, [Validators.required, Validators.pattern(/^[A-Za-z0-9_]+$/)]],
      Tolerance: [n.Tolerance ?? '', nonNegative], Limit: [n.Limit ?? '', positive],
      LowerLimit: [n.LowerLimit ?? '', finiteNumber], UpperLimit: [n.UpperLimit ?? '', finiteNumber],
      LimitValue: [n.LimitValue ?? 3, finiteNumber], DecisionRule: [n.DecisionRule ?? '', [Validators.required, Validators.pattern(/^[0-5]$/)]],
      Distribution: this.distribution(n.Distribution)
    }, { validators: acceptanceLimits });
    const row = this.fb.group({ NModel: model, IIndication: [value.IIndication ?? '', finiteNumber],
      Wm_cr: this.fb.group({ Value: [w.Value ?? '', finiteNumber], ExpandedUncertainty: [w.ExpandedUncertainty ?? '', nonNegative], CoverageFactor: [w.CoverageFactor ?? '', positive] }),
      Dmcal_D: this.rectangular(value.Dmcal_D), DmB: this.rectangular(value.DmB), DIresolution: this.rectangular(value.DIresolution) });
    this.Points.push(row);
    if (inherit && this.Points.length > 1) { this.useAcceptanceDefault(this.Points.length - 1); }
    let previous = JSON.stringify(this.acceptanceValue(model));
    this.formSubscriptions.add(model.valueChanges.subscribe(() => {
      const current = JSON.stringify(this.acceptanceValue(model));
      if (previous === current) { return; } previous = current;
      if (this.applyingDefaults) { return; }
      const index = this.Points.controls.indexOf(row);
      if (index === 0 && this.validAcceptance(model)) {
        this.Points.controls.slice(1).filter(other => !this.overrides.has(other)).forEach(other => this.applyDefault(other));
      } else if (index > 0) { this.overrides.add(row); }
    }));
  }
  removePoint(index: number): void {
    this.Points.removeAt(index);
    if (this.Points.length) {
      this.overrides.delete(this.Points.at(0));
      if (this.validAcceptance(this.nModel(this.Points.at(0)))) { this.Points.controls.slice(1).filter(row => !this.overrides.has(row)).forEach(row => this.applyDefault(row)); }
    }
  }
  private acceptanceValue(model: AbstractControl): any { const result: any = {}; this.acceptanceFields.forEach(key => result[key] = model.get(key).value); return result; }
  private validAcceptance(model: AbstractControl): boolean { return !!model && this.acceptanceFields.every(key => model.get(key).valid) && !model.hasError('acceptanceLimits'); }
  private applyDefault(row: AbstractControl): void { this.applyingDefaults = true; this.nModel(row).patchValue(this.acceptanceValue(this.nModel(this.Points.at(0)))); this.applyingDefaults = false; }
  useAcceptanceDefault(index: number): void { if (index > 0 && this.validAcceptance(this.nModel(this.Points.at(0)))) { this.applyDefault(this.Points.at(index)); this.overrides.delete(this.Points.at(index)); } }
  isAcceptanceOverride(row: AbstractControl): boolean { return this.overrides.has(row); }
  addReading(): void { this.readings.push(this.fb.control('', finiteNumber)); }
  removeReading(index: number): void { this.readings.removeAt(index); }
  addCode(): void { this.codes.push(this.fb.control('', Validators.required)); }
  removeCode(index: number): void { this.codes.removeAt(index); }

  buildPayload(): BalanceRequest {
    if (this.form.invalid || this.legacyWarning) { throw new Error('فرم نامعتبر است؛ فیلدهای مشخص‌شده را بررسی کنید.'); }
    const raw = this.form.getRawValue(), payload: any = {};
    const number = (x: any): number => { if (x === null || x === undefined || String(x).trim() === '' || typeof x === 'boolean' || !Number.isFinite(Number(x))) { throw new Error('مقدار عددی نامعتبر است.'); } return Number(x); };
    const constant = (value: any, unit = 'g') => ({ Value: number(value), Unit: unit });
    const distribution = (d: any) => ({ $type: d.$type, CoverageFactor: number(d.CoverageFactor), ShapeFactor: number(d.ShapeFactor), ...(d.$type === 'StudentT' ? { DoF: number(d.DoF) } : {}) });
    const rectangular = (value: any, unit = 'g') => ({ ...constant(value.Value, unit), HalfWidthOfLimit: number(value.HalfWidthOfLimit), RelativeErrorOfUncertainty: number(value.RelativeErrorOfUncertainty), Distribution: { $type: 'Rectangular', CoverageFactor: 1.65, ShapeFactor: 1 } });
    [...this.generalFields, ...this.deviceFields, 'RequestId'].forEach(key => payload[key] = String(raw[key] ?? '').trim());
    [...this.rangeFields, 'LetterId', 'CalibrationDetailId'].forEach(key => payload[key] = number(raw[key]));
    this.environmentFields.forEach(key => payload[key] = raw[key] == null || String(raw[key]).trim() === '' ? null : number(raw[key]));
    payload.Unit = 'g'; payload.Farsi = !!raw.Farsi; payload.BehfaDeviceCodes = raw.BehfaDeviceCodes.map(x => String(x).trim());
    payload.AcceptanceOverrides = this.Points.controls.filter(row => this.overrides.has(row)).map(row => String(row.get('NModel.MeasurementId').value));
    payload.NModels = raw.Points.map(point => { const n = point.NModel; const row: any = { MeasurementId: String(n.MeasurementId), Value: String(n.Value), Distribution: distribution(n.Distribution) }; [...this.acceptanceNumbers, 'DecisionRule'].forEach(key => row[key] = number(n[key])); return row; });
    payload.IIndication = raw.Points.map(point => constant(point.IIndication));
    payload.Wm_cr = raw.Points.map(point => ({ ...constant(point.Wm_cr.Value), ExpandedUncertainty: number(point.Wm_cr.ExpandedUncertainty), CoverageFactor: number(point.Wm_cr.CoverageFactor), Distribution: { $type: 'Normal', CoverageFactor: 2, ShapeFactor: 1 } }));
    this.pointTypeBFields.forEach(key => payload[key] = raw.Points.map(point => rectangular(point[key])));
    this.sharedTypeBFields.forEach(key => payload[key] = rectangular(raw[key], key === 'Dk' ? '' : 'g'));
    [...this.eccentricityFields, 'Rrepeatability'].forEach(key => payload[key] = constant(raw[key]));
    if (raw.Lrep !== '' && raw.Lrep != null) { payload.Lrep = constant(raw.Lrep); }
    payload.DRrepeatability = { Observations: raw.DRrepeatability.Observations.map(number), UncertaintyEstimate: number(raw.DRrepeatability.UncertaintyEstimate), ObservationMethod: 0, UncertaintyEvaluation: 0, Unit: 'g' };
    return payload;
  }
  private rememberResetOutput(): void {
    this.resetOutput = { result: this.result, certificate: this.certificateFilePath, report: this.uncertaintyReportFilePath, legacyWarning: this.legacyWarning };
  }
  resetForm(): void {
    if (this.isSubmitting || this.isLoadingInitialData || this.isDownloading) { return; }
    const output = this.resetOutput;
    this.initializeForm(this.resetSnapshot);
    this.result = output.result; this.certificateFilePath = output.certificate; this.uncertaintyReportFilePath = output.report;
    this.legacyWarning = output.legacyWarning; this.rememberResetOutput();
  }
  toggleResult(index: number): void { this.expandedResults.has(index) ? this.expandedResults.delete(index) : this.expandedResults.add(index); }
  get certificateDescription(): string { return this.displayText(this.form?.get('Description').value || this.result?.Description || this.result?.GufModel?.Description); }
  get resultCount(): number { return this.formulas.length || this.resultRows.length; }
  calculate(): void { this.submit(false); }
  submitForm(): void { this.submit(true); }
  private submit(save: boolean): void {
    if (this.isSubmitting || this.isLoadingInitialData) { return; }
    this.submitAttempted = true; this.errorMessage = ''; this.form.markAllAsTouched();
    if (this.form.invalid || this.legacyWarning) { this.Points.controls.filter(row => row.invalid).forEach(row => this.collapsedPoints.delete(row)); this.host.nativeElement.querySelector('.ng-invalid:not(form):not(fieldset)')?.focus(); return; }
    if (save && (this.Points.length !== 5 || !this.positiveId(this.form.get('CalibrationDetailId').value))) { this.errorMessage = 'ثبت گواهی به پنج نقطه و شناسه معتبر جزئیات کالیبراسیون نیاز دارد.'; return; }
    this.isSubmitting = true; this.pendingAction = save ? 'save' : 'preview';
    (save ? this.service.save(this.buildPayload()) : this.service.calculate(this.buildPayload())).pipe(takeUntil(this.destroy$), finalize(() => { this.isSubmitting = false; this.pendingAction = null; })).subscribe(response => {
      if (save) { this.applyResponse(response); this.alertify.SuccessMessage('ثبت اطلاعات با موفقیت انجام شد.'); }
      else { this.result = this.normalize(response); }
    }, error => this.applyError(error));
  }
  private load(): void {
    const id = this.inputId || this.routeId; this.loadSubscription?.unsubscribe();
    if (!id) { return; }
    this.isLoadingInitialData = true;
    this.loadSubscription = this.service.get(id).pipe(takeUntil(this.destroy$), finalize(() => this.isLoadingInitialData = false)).subscribe(response => this.applyResponse(response), error => { this.initializeForm({ CalibrationDetailId: id }); this.applyError(error); });
  }
  applyResponse(response: any): void {
    const envelope = response?.data ?? response ?? {}, s = this.normalize(envelope);
    if (s.balanceModel) { this.initializeForm(s.balanceModel); }
    if (Array.isArray(s.ReferenceStandards)) { this.referenceStandards = s.ReferenceStandards; }
    this.result = s.certificate || null;
    this.certificateFilePath = s.certificateUrl || this.certificateFilePath;
    this.uncertaintyReportFilePath = s.uncertaintyReportUrl || this.uncertaintyReportFilePath;
    this.expandedResults = new Set<number>([0]); this.rememberResetOutput();
  }
  private applyError(error: any): void {
    this.errorMessage = typeof error.error === 'string' ? error.error : 'خطا در پردازش اطلاعات؛ فیلدهای فرم و اتصال به سرور را بررسی کنید.';
    Object.keys(error.error?.errors || {}).forEach(path => {
      const normalized = path.replace(/\[(\d+)\]/g, '.$1');
      const parts = normalized.split('.'), key = parts[0], index = Number(parts[1]);
      let control: AbstractControl;
      const pointKeys = ['nModels', 'IIndication', 'Wm_cr', ...this.pointTypeBFields];
      if (pointKeys.some(k => k.toLowerCase() === key.toLowerCase()) && Number.isInteger(index)) {
        const actual = pointKeys.find(k => k.toLowerCase() === key.toLowerCase());
        const tail = parts.slice(2).map(x => x.charAt(0).toUpperCase() + x.slice(1));
        control = this.Points.at(index)?.get([actual === 'nModels' ? 'NModel' : actual, ...tail]);
      } else {
        const actual = Object.keys(this.form.controls).find(k => k.toLowerCase() === key.toLowerCase());
        control = actual ? this.form.get([actual, ...parts.slice(1).map(x => x.charAt(0).toUpperCase() + x.slice(1))]) : null;
      }
      if (control) { control.setErrors({ ...control.errors, server: true }); control.markAsTouched(); }
    });
  }
  validationMessage(control: AbstractControl): string {
    if (!control || !(control.touched || this.submitAttempted)) { return ''; }
    if (control.parent?.hasError('acceptanceLimits') && (control === control.parent.get('LowerLimit') || control === control.parent.get('UpperLimit'))) { return 'حد پایین باید کوچک‌تر از حد بالا باشد.'; }
    if (!control.invalid) { return ''; }
    if (control.hasError('required')) { return 'این فیلد الزامی است.'; }
    if (control.hasError('positive')) { return 'عدد بزرگ‌تر از صفر وارد کنید.'; }
    if (control.hasError('nonNegative')) { return 'عدد منفی مجاز نیست.'; }
    if (control.hasError('server')) { return 'سرور این مقدار را نپذیرفت؛ مقدار را بازبینی کنید.'; }
    return 'مقدار معتبر وارد کنید.';
  }
  // Presentation only: probabilities from this API are already percentages.
  private numeric(value: any): number {
    return value == null || typeof value === 'boolean' || String(value).trim() === '' || !Number.isFinite(Number(value)) ? null : Number(value);
  }
  displayNumber(value: any, decimals = 8): string {
    const n = this.numeric(value); return n === null ? '---' : n.toFixed(decimals);
  }
  displayUncertainty(value: any): string {
    const n = this.numeric(value); if (n === null) { return '---'; }
    const mg = n * 1000;
    if (!Number.isFinite(mg)) { return '---'; }
    if (mg === 0) { return '0'; }
    return mg.toFixed(Math.min(15, Math.max(0, 1 - Math.floor(Math.log10(Math.abs(mg))))));
  }
  displayProbability(value: any): string {
    const n = this.numeric(value); return n === null ? '---' : n > 0 && n < 0.001 ? '< 0.001%' : n > 99.999 && n < 100 ? '> 99.999%' : n.toFixed(3) + '%';
  }
  displayTur(value: any): string { const n = this.numeric(value); return n === null ? '---' : n.toFixed(1) + ' : 1'; }
  statusLabel(value: any): string { return value === true ? 'قبول' : value === false ? 'رد' : '---'; }
  pointLabel(index: number, referenceMass?: any): string {
    const row = this.Points.at(index), id = row?.get('NModel.Value').value;
    const label = id === 'zero' || index === 0 ? 'نقطه صفر' : 'نقطه ' + index;
    const mass = this.numeric(referenceMass ?? row?.get('Wm_cr.Value').value);
    return label + (mass === null ? '' : ' — ' + mass.toLocaleString('en-US', { maximumFractionDigits: 9, useGrouping: false }) + ' g');
  }
  togglePoint(row: AbstractControl): void { this.collapsedPoints.has(row) ? this.collapsedPoints.delete(row) : this.collapsedPoints.add(row); }
  get resultRows(): any[] {
    if (Array.isArray(this.result?.Points)) { return this.result.Points; }
    return this.formulas.map(f => ({ Tolerance: f.Result?.CapabilityProof?.Tolerance, CapabilityLimit: f.Result?.CapabilityProof?.Limit, EffectiveDegreesOfFreedom: f.Result?.DoF, Error: f.Result?.Value, StandardUncertainty: f.Result?.StandardUncertainty,
      ExpandedUncertainty: f.Result?.ExpandedUncertainty, CoverageFactor: f.Result?.CoverageFactor,
      Tur: f.Result?.CapabilityProof?.Index, Capability: f.Result?.CapabilityProof?.Acceptance,
      Compliance: f.Result?.ComplianceAssessment?.Acceptance, PInside: f.Result?.ComplianceAssessment?.InsideProbability,
      POutside: f.Result?.ComplianceAssessment?.OutsideProbability }));
  }
  get regressionResult(): any { return this.result?.Regression || this.result?.GufModel?.Regression; }
  displayText(value: any): string { return typeof value === 'string' && value.trim() && !/NaN|Infinity|undefined|null/.test(value) ? value : '---'; }
  get formulas(): any[] { return this.result?.Formulas || this.result?.GufModel?.Formulas || []; }
  get repeatResult(): any { return this.result?.Repeatability || this.formulas[0]?.Parameters?.find(x => x.$type === 'TypeA' || x.Name === 'DRrepeatability'); }
  get eccentricityResult(): any {
    if (this.result?.Eccentricity) { return this.result.Eccentricity; }
    // Legacy preview may omit the server eccentricity summary; never derive a result in Angular.
    return null;
  }
  download(path: string): void {
    if (!path || this.isDownloading || this.isSubmitting || this.isLoadingInitialData) { return; }
    this.isDownloading = true; this.downloadingPath = path; this.errorMessage = '';
    this.service.downloadFile(path).pipe(takeUntil(this.destroy$), finalize(() => { this.isDownloading = false; this.downloadingPath = ''; })).subscribe(blob => {
      const url = URL.createObjectURL(blob), link = document.createElement('a'); link.href = url; link.download = path.split(/[\\/]/).pop(); link.click(); URL.revokeObjectURL(url);
    }, () => { this.errorMessage = 'خطا در دریافت فایل.'; });
  }
  private positiveId(value: any): number { const id = Number(value); return Number.isInteger(id) && id > 0 ? id : null; }
  private normalize(value: any): any {
    if (Array.isArray(value)) { return value.map(x => this.normalize(x)); }
    if (!value || typeof value !== 'object') { return value; }
    const aliases: any = { BalanceModel: 'balanceModel', Certificate: 'certificate', CertificateUrl: 'certificateUrl', UncertaintyReportUrl: 'uncertaintyReportUrl', balanceModel: 'balanceModel', certificate: 'certificate', certificateUrl: 'certificateUrl', uncertaintyReportUrl: 'uncertaintyReportUrl', uncertaintyReportFile: 'uncertaintyReportFile', gufModel: 'GufModel', iIndication: 'IIndication', wm_cr: 'Wm_cr', dm_cal: 'Dm_cal', dmcal_D: 'Dmcal_D', dmB: 'DmB', dIresolution: 'DIresolution', dIresolution_0: 'DIresolution_0', dRrepeatability: 'DRrepeatability', rrepeatability: 'Rrepeatability', dk: 'Dk', iCenter: 'ICenter', iFront_L: 'IFront_L', iBack_L: 'IBack_L', iFront_R: 'IFront_R', iBack_R: 'IBack_R', lecc: 'Lecc', lrep: 'Lrep', doF: 'DoF' };
    const result: any = {};
    Object.keys(value).forEach(key => result[key === '$type' ? key : aliases[key] || key.charAt(0).toUpperCase() + key.slice(1)] = this.normalize(value[key]));
    return result;
  }
}
